import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Button, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, alpha, IconButton, Tooltip,
  LinearProgress, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Warning, Phone, CheckCircle, Escalator, Refresh, Sos, Place,
  ArrowBack, DoneAll, FiberManualRecord, Map as MapIcon,
} from '@mui/icons-material';
import {
  listDispatchSos, acknowledgeSos, resolveSos, escalateSos,
} from '../../services/dispatcherService';
import { useDispatchSocket } from '../../hooks/useDispatchSocket';
import DispatchRouteMap from '../../components/dispatcher/DispatchRouteMap';
import {
  DispatchSosAlert, SosScope, SosSeverity,
  SOS_STATUS_META, SOS_SEVERITY_META, SOS_SOURCE_LABEL,
  sosUserName, sosUserPhone, timeSince, sosWorkflowStep,
} from '../../components/dispatcher/sosTypes';
import { DISPATCH, dispatchScroll, panelShell } from '../../components/dispatcher/dispatchTheme';

const SCOPE_TABS: { key: SosScope; label: string }[] = [
  { key: 'active', label: 'Active Alerts' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All History' },
];

const WORKFLOW_STEPS = ['Received', 'Acknowledged', 'Escalated', 'Resolved'];

export default function DispatchSosPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [scope, setScope] = useState<SosScope>('active');
  const [severityFilter, setSeverityFilter] = useState<SosSeverity | 'all'>('all');
  const [selected, setSelected] = useState<DispatchSosAlert | null>(null);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [report, setReport] = useState('');
  const [error, setError] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['dispatch-sos'] });
  useDispatchSocket((event) => { if (event === 'dispatch:sos') invalidate(); });

  const { data: sosList = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['dispatch-sos', scope, severityFilter],
    queryFn: () => listDispatchSos({
      scope,
      severity: severityFilter === 'all' ? undefined : severityFilter,
    }),
    refetchInterval: 8000,
  });

  const ackMut = useMutation({
    mutationFn: acknowledgeSos,
    onSuccess: () => { setError(''); invalidate(); },
    onError: (e: unknown) => setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Acknowledge failed'),
  });

  const resolveMut = useMutation({
    mutationFn: ({ id, incidentReport }: { id: string; incidentReport: string }) => resolveSos(id, { incidentReport }),
    onSuccess: () => {
      setResolveId(null);
      setReport('');
      setError('');
      setSelected(null);
      invalidate();
    },
    onError: (e: unknown) => setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Resolve failed'),
  });

  const escMut = useMutation({
    mutationFn: escalateSos,
    onSuccess: () => { setError(''); invalidate(); },
    onError: (e: unknown) => setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Escalate failed'),
  });

  const stats = useMemo(() => ({
    critical: sosList.filter((s) => s.severity === 'critical' && s.status !== 'resolved').length,
    active: sosList.filter((s) => s.status === 'active').length,
    acknowledged: sosList.filter((s) => s.status === 'acknowledged').length,
    escalated: sosList.filter((s) => s.status === 'escalated').length,
  }), [sosList]);

  const selectedCoords = selected?.location?.coordinates;
  const mapCenter = selectedCoords
    ? { lat: selectedCoords[1], lng: selectedCoords[0] }
    : undefined;

  const workflowStep = selected ? sosWorkflowStep(selected.status) : 0;

  useEffect(() => {
    if (!selected) return;
    const fresh = sosList.find((s) => s._id === selected._id);
    if (fresh) setSelected(fresh);
  }, [sosList, selected?._id]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'calc(100dvh - 48px)', sm: 'calc(100dvh - 52px)' }, minHeight: 520, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #450a0a 0%, #7f1d1d 45%, #991b1b 100%)', color: '#fff', px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2 }, flexShrink: 0, boxShadow: DISPATCH.shadowLg }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
            <IconButton size="small" onClick={() => navigate('/dispatcher')} sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.25)', mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.14)', flexShrink: 0 }}>
              <Sos />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2} noWrap>SOS Command Center</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Emergency response · acknowledge · escalate · resolve</Typography>
            </Box>
          </Stack>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()} sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={1.5}>
          <Chip label={`${stats.critical} critical`} size="small" sx={{ fontWeight: 800, bgcolor: alpha('#fff', 0.15), color: '#fecaca' }} />
          <Chip label={`${stats.active} active`} size="small" sx={{ fontWeight: 800, bgcolor: alpha('#ef4444', 0.35), color: '#fff' }} />
          <Chip label={`${stats.acknowledged} ack`} size="small" sx={{ fontWeight: 800, bgcolor: alpha('#fbbf24', 0.3), color: '#fef3c7' }} />
          <Chip label={`${stats.escalated} escalated`} size="small" sx={{ fontWeight: 800, bgcolor: alpha('#dc2626', 0.4), color: '#fff' }} />
          <Chip icon={<FiberManualRecord sx={{ fontSize: '8px !important', color: '#fca5a5 !important' }} />} label="Live" size="small"
            sx={{ fontWeight: 800, bgcolor: 'rgba(255,255,255,0.1)', color: '#fecaca', border: '1px solid rgba(252,165,165,0.35)' }} />
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {SCOPE_TABS.map((t) => (
            <Button
              key={t.key}
              size="small"
              onClick={() => { setScope(t.key); setSelected(null); }}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                color: scope === t.key ? '#7f1d1d' : 'rgba(255,255,255,0.9)',
                bgcolor: scope === t.key ? '#fff' : 'rgba(255,255,255,0.1)',
                border: '1px solid',
                borderColor: scope === t.key ? '#fff' : 'rgba(255,255,255,0.2)',
              }}
            >
              {t.label}
            </Button>
          ))}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: { xs: 1, md: 2 }, mt: 1, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, minHeight: 0, overflow: 'hidden', gap: { lg: 1 }, p: { xs: 0, lg: 1 }, bgcolor: DISPATCH.bgMuted }}>
        {/* Alert list */}
        <Box sx={{ width: { lg: 420 }, maxWidth: { lg: 420 }, flexShrink: 0, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', ...panelShell({ height: { xs: selected && isMobile ? 0 : 'auto', lg: '100%' }, flex: { xs: selected && isMobile ? 0 : 1, lg: '0 0 auto' }, borderRadius: { xs: 0, lg: `${DISPATCH.radius}px` } }) }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${DISPATCH.border}`, flexShrink: 0 }}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(['all', 'critical', 'high', 'medium', 'low'] as const).map((sev) => (
                <Chip
                  key={sev}
                  label={sev === 'all' ? 'All severity' : SOS_SEVERITY_META[sev].label}
                  size="small"
                  onClick={() => setSeverityFilter(sev)}
                  sx={{
                    fontWeight: 700,
                    height: 26,
                    bgcolor: severityFilter === sev ? alpha(SOS_SEVERITY_META[sev === 'all' ? 'high' : sev].color, 0.12) : 'transparent',
                    color: severityFilter === sev ? (sev === 'all' ? DISPATCH.primary : SOS_SEVERITY_META[sev].color) : 'text.secondary',
                    border: 1,
                    borderColor: severityFilter === sev ? alpha(SOS_SEVERITY_META[sev === 'all' ? 'high' : sev].color, 0.35) : 'divider',
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ ...dispatchScroll, p: 1.25 }}>
            {(isLoading || isFetching) && sosList.length === 0 && (
              <Box py={4} textAlign="center"><LinearProgress sx={{ mb: 2 }} />Loading alerts…</Box>
            )}
            {!isLoading && sosList.length === 0 && (
              <Box textAlign="center" py={6} px={2}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography fontWeight={800} mb={0.5}>No SOS alerts</Typography>
                <Typography variant="caption" color="text.secondary">
                  {scope === 'active' ? 'All clear — no emergencies right now' : 'No records for this filter'}
                </Typography>
              </Box>
            )}
            {sosList.map((s) => {
              const sev = SOS_SEVERITY_META[s.severity];
              const st = SOS_STATUS_META[s.status];
              const isSelected = selected?._id === s._id;
              return (
                <Box
                  key={s._id}
                  onClick={() => setSelected(s)}
                  sx={{
                    mb: 0.75,
                    p: 1.25,
                    borderRadius: `${DISPATCH.radiusSm}px`,
                    cursor: 'pointer',
                    bgcolor: isSelected ? '#fff' : alpha('#fff', 0.92),
                    border: '2px solid',
                    borderColor: isSelected ? sev.color : DISPATCH.border,
                    boxShadow: isSelected ? DISPATCH.shadowSelected : DISPATCH.shadow,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5} flexWrap="wrap" useFlexGap>
                    <Warning sx={{ fontSize: 18, color: sev.color }} />
                    <Typography variant="subtitle2" fontWeight={800} noWrap>{s.sosNumber}</Typography>
                    <Chip label={sev.label} size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: alpha(sev.color, 0.12), color: sev.color }} />
                    <Chip label={st.label} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, borderColor: alpha(st.color, 0.4), color: st.color }} />
                  </Stack>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {SOS_SOURCE_LABEL[s.sourceType]} · {sosUserName(s.sourceUserId)}
                  </Typography>
                  {s.location?.address && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {s.location.address}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" display="block" mt={0.25}>{timeSince(s.createdAt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Detail + map */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {!selected ? (
            <Box sx={{ ...panelShell(), flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
              <Sos sx={{ fontSize: 56, color: alpha('#ef4444', 0.4), mb: 2 }} />
              <Typography variant="h6" fontWeight={800} mb={0.5}>Select an alert</Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={360}>
                Choose an SOS alert from the list to view location, contact details, and run the response workflow.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ ...panelShell(), p: 2, flexShrink: 0 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} mb={1.5}>
                  <Box minWidth={0}>
                    <Typography variant="h6" fontWeight={800}>{selected.sosNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {SOS_SOURCE_LABEL[selected.sourceType]} · {sosUserName(selected.sourceUserId)}
                      {selected.entityType && ` · ${selected.entityType.replace(/_/g, ' ')}`}
                    </Typography>
                  </Box>
                  {isMobile && (
                    <Button size="small" onClick={() => setSelected(null)}>Back</Button>
                  )}
                </Stack>

                {/* Workflow */}
                <Box mb={2}>
                  <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.06em" display="block" mb={1}>
                    Response Workflow
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(workflowStep / WORKFLOW_STEPS.length) * 100}
                    sx={{ mb: 1, height: 6, borderRadius: 3, bgcolor: alpha('#ef4444', 0.1), '& .MuiLinearProgress-bar': { bgcolor: selected.status === 'resolved' ? DISPATCH.success : '#ef4444' } }}
                  />
                  <Stack direction="row" justifyContent="space-between">
                    {WORKFLOW_STEPS.map((label, i) => (
                      <Typography key={label} variant="caption" fontWeight={workflowStep > i ? 800 : 500} color={workflowStep > i ? 'success.main' : workflowStep === i + 1 ? 'error.main' : 'text.disabled'} sx={{ fontSize: '0.62rem' }}>
                        {label}
                      </Typography>
                    ))}
                  </Stack>
                </Box>

                {selected.notes && (
                  <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }} icon={<Warning fontSize="inherit" />}>
                    {selected.notes}
                  </Alert>
                )}

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {sosUserPhone(selected.sourceUserId) && (
                    <Button size="small" variant="outlined" startIcon={<Phone />} href={`tel:${sosUserPhone(selected.sourceUserId)}`}>
                      Call {sosUserPhone(selected.sourceUserId)}
                    </Button>
                  )}
                  {selected.status === 'active' && (
                    <Button size="small" variant="contained" color="warning" disabled={ackMut.isPending}
                      onClick={() => ackMut.mutate(selected._id)}>
                      1. Acknowledge
                    </Button>
                  )}
                  {['active', 'acknowledged'].includes(selected.status) && (
                    <Button size="small" variant="outlined" color="error" startIcon={<Escalator />} disabled={escMut.isPending}
                      onClick={() => escMut.mutate(selected._id)}>
                      2. Escalate
                    </Button>
                  )}
                  {selected.status !== 'resolved' && (
                    <Button size="small" variant="contained" color="success" startIcon={<DoneAll />}
                      onClick={() => setResolveId(selected._id)}>
                      3. Resolve & Report
                    </Button>
                  )}
                </Stack>

                {selected.status === 'resolved' && selected.incidentReport && (
                  <Box mt={1.5} p={1.5} borderRadius={2} bgcolor={alpha(DISPATCH.success, 0.08)} border={`1px solid ${alpha(DISPATCH.success, 0.25)}`}>
                    <Typography variant="caption" fontWeight={800} color="success.main" display="block" mb={0.5}>Incident Report</Typography>
                    <Typography variant="body2">{selected.incidentReport}</Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ ...panelShell(), flex: 1, minHeight: { xs: 280, lg: 200 }, overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" spacing={0.75} px={1.5} py={1} borderBottom={`1px solid ${DISPATCH.border}`}>
                  <MapIcon fontSize="small" color="error" />
                  <Typography variant="subtitle2" fontWeight={800}>Alert Location</Typography>
                  {selected.location?.address && (
                    <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {selected.location.address}
                    </Typography>
                  )}
                </Stack>
                <Box sx={{ height: 'calc(100% - 44px)', minHeight: 240 }}>
                  {selectedCoords ? (
                    <DispatchRouteMap
                      height="100%"
                      center={mapCenter}
                      zoom={15}
                      markers={[{
                        id: 'sos', lat: mapCenter.lat, lng: mapCenter.lng,
                        label: `${selected.sosNumber} · ${SOS_SEVERITY_META[selected.severity].label}`,
                        color: SOS_SEVERITY_META[selected.severity].color,
                        kind: 'dropoff',
                      }]}
                      route={[]}
                      activePin="dropoff"
                    />
                  ) : (
                    <Box height="100%" display="flex" alignItems="center" justifyContent="center" bgcolor={alpha(DISPATCH.bgMuted, 0.5)}>
                      <Stack alignItems="center" spacing={0.5}>
                        <Place color="disabled" />
                        <Typography variant="caption" color="text.secondary">No GPS coordinates for this alert</Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={!!resolveId} onClose={() => setResolveId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Resolve SOS & File Incident Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Document what happened and actions taken. This is required before closing the alert.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Incident report *"
            placeholder="Describe the situation, contacts made, and resolution steps…"
            value={report}
            onChange={(e) => setReport(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!report.trim() || resolveMut.isPending}
            onClick={() => resolveId && resolveMut.mutate({ id: resolveId, incidentReport: report.trim() })}
          >
            Resolve Alert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
